import type { ContactEmail, ContactFullModel, ContactPhone } from '~/server_shared/swagger/data-contracts';

// Extended contact type that includes the computed primary_email and primary_phone properties
export interface ContactWithPrimaryEmailAndPhone extends ContactFullModel {
  primary_email?: ContactEmail;
  primary_phone?: ContactPhone;
}

/**
 * Checks if a ContactEmail has a non-empty email value and is not removed
 * @param email - The ContactEmail object to check
 * @returns true if email has content and is not removed
 */
export const isNonEmptyNotRemovedContactEmail = (email: ContactEmail): boolean => {
  return Boolean(email.email && email.email.trim() !== '' && !email.is_removed);
};

/**
 * Checks if a ContactPhone has a non-empty phone value and is not removed
 * @param phone - The ContactPhone object to check
 * @returns true if phone has content and is not removed
 */
export const isNonEmptyNotRemovedContactPhone = (phone: ContactPhone): boolean => {
  return Boolean(phone.phone && phone.phone.trim() !== '' && !phone.is_removed);
};

/**
 * Gets the primary phone for a contact - prefers mobile, falls back to first phone
 * @param contact - The contact object with phones array
 * @returns Primary phone or undefined if no valid phones found
 */
export const getContactPrimaryPhone = (contact: ContactFullModel): ContactPhone | undefined => {
  if (!contact.phones || contact.phones.length === 0) {
    return undefined;
  }

  // First try to find a valid mobile phone
  const mobilePhones = contact.phones.filter(
    (phone: ContactPhone) => phone.phone_type === 'mobile' && isNonEmptyNotRemovedContactPhone(phone)
  );
  if (mobilePhones.length > 0) {
    return mobilePhones[0];
  }

  // Fallback to first valid phone
  const firstValidPhone = contact.phones.find(isNonEmptyNotRemovedContactPhone);
  return firstValidPhone;
};

/**
 * Gets the primary email for a contact - first non-empty, not removed email
 * @param contact - The contact object with emails array
 * @returns Primary email or undefined if no valid emails found
 */
export const getContactPrimaryEmail = (contact: ContactFullModel): ContactEmail | undefined => {
  if (!contact.emails || contact.emails.length === 0) {
    return undefined;
  }

  // Find first valid email
  const firstValidEmail = contact.emails.find(isNonEmptyNotRemovedContactEmail);
  return firstValidEmail;
};

/**
 * Populates a contact with computed primary_email and primary_phone properties
 * @param contact - The contact object to populate
 * @returns Contact with primary_email and primary_phone properties
 */
export const contactPopulate = (contact: ContactFullModel): ContactWithPrimaryEmailAndPhone => ({
  ...contact,
  primary_email: getContactPrimaryEmail(contact),
  primary_phone: getContactPrimaryPhone(contact),
});

/**
 * Determines whether communication permissions should be checked for a contact
 * If you update this function, make sure you update should_check_for_communication_permissions as well (BE) and Contact.jsx if still exists
 * @param isRequiredForAllContacts - The organization's Communications setting that extends the check to companies and vendors
 * @param isContactCompany - Whether the contact is a company
 * @param contactRole - The role of the contact (e.g., 'vendor')
 * @returns true if communication permissions should be checked
 */
export const shouldCheckForCommunicationPermissions = (
  isRequiredForAllContacts: boolean,
  isContactCompany: boolean,
  contactRole: string
): boolean => {
  return isRequiredForAllContacts || (!isContactCompany && contactRole !== 'vendor');
};
